import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Send, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { getConsent } from '../lib/consent';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Map query-string interest value -> Finnish dropdown option label.
const interestQueryMap = {
  demo: 'Demo',
  'brand-agent': 'Brändiagentti',
  'brandiagentti': 'Brändiagentti',
  'ai-agentti': 'Brändiagentti',
  'ai-agent': 'Brändiagentti',
  automaatiot: 'Automaatiot',
  automation: 'Automaatiot',
  automations: 'Automaatiot',
  sisalto: 'Sisältö & brändihahmo',
  content: 'Sisältö & brändihahmo',
};

const ContactSection = () => {
  const { t } = useLanguage();
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    interest: '',
    message: '',
    website: '' // honeypot — must stay empty
  });
  const [status, setStatus] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Preselect "Kiinnostuksen kohde" from ?interest=demo (etc.)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const raw = (params.get('interest') || '').toLowerCase();
    if (!raw) return;
    const mapped = interestQueryMap[raw];
    if (mapped && t.contact.form.interestOptions.includes(mapped)) {
      setFormData((p) => (p.interest ? p : { ...p, interest: mapped }));
    }
  }, [location.search, t.contact.form.interestOptions]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setStatus(null);

    // Shared event ID for Meta Pixel <-> CAPI deduplication
    const eventId =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `lead-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const pageUrl = typeof window !== 'undefined' ? window.location.href : '';

    try {
      await axios.post(`${API_BASE}/contact`, {
        name: formData.name.trim(),
        company: formData.company.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        interest: formData.interest,
        message: formData.message.trim(),
        website: formData.website, // honeypot
        event_id: eventId,
        page_url: pageUrl,
        marketing_consent: getConsent()?.marketing === true
      });

      // Meta Pixel: track Lead conversion (eventID enables dedup with CAPI)
      if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
        window.fbq(
          'track',
          'Lead',
          { content_name: 'Contact Form Lead' },
          { eventID: eventId }
        );
      }

      setStatus('success');
      setFormData({
        name: '',
        company: '',
        email: '',
        phone: '',
        interest: '',
        message: '',
        website: ''
      });
    } catch (err) {
      console.error('Contact submission failed:', err);
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-bg" data-testid="contact-section">
      <div className="container-page">
        <div className="grid md:grid-cols-12 gap-12">
          {/* Heading column */}
          <div className="md:col-span-5">
            <span className="eyebrow text-ink-muted">{t.contact.cta}</span>
            <h2 className="mt-5 text-h2 text-ink">{t.contact.title}</h2>
            <p className="mt-5 text-body-lg text-ink-muted max-w-md">{t.contact.subtitle}</p>

            <dl className="mt-10 space-y-5 text-[14.5px]">
              <div>
                <dt className="text-eyebrow uppercase text-ink-muted">Sähköposti</dt>
                <dd className="mt-1.5"><a href="mailto:agencive.ai@gmail.com" className="text-ink hover:text-brand">agencive.ai@gmail.com</a></dd>
              </div>
              <div>
                <dt className="text-eyebrow uppercase text-ink-muted">Puhelin</dt>
                <dd className="mt-1.5 flex gap-3">
                  <a href="tel:+358401601975" className="text-ink hover:text-brand">+358 40 1601975</a>
                  <span className="text-line">·</span>
                  <a href="tel:+358449766413" className="text-ink hover:text-brand">+358 44 9766413</a>
                </dd>
              </div>
              <div>
                <dt className="text-eyebrow uppercase text-ink-muted">Sijainti</dt>
                <dd className="mt-1.5 text-ink-muted">Lempäälä · Pirkanmaa · Suomi</dd>
              </div>
            </dl>
          </div>

          {/* Form column */}
          <div className="md:col-span-7">
            <div
              id="contact-form"
              data-testid="contact-form-anchor"
              className="rounded-card bg-surface border border-line p-7 md:p-10 scroll-mt-24"
            >
              <form
                onSubmit={handleSubmit}
                method="post"
                action={`${API_BASE}/contact`}
                className="space-y-5"
                data-testid="contact-form"
                data-contact-form
                aria-label="Yhteydenottolomake"
              >
                {/* Honeypot — hidden from real users, filled by bots. Do not remove. */}
                <div
                  aria-hidden="true"
                  style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}
                >
                  <label htmlFor="cf-website">Website</label>
                  <input
                    id="cf-website"
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.website}
                    onChange={handleChange}
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div className="space-y-1.5">
                    <label htmlFor="cf-name" className="text-[12.5px] font-medium text-ink-muted">{t.contact.form.name}</label>
                    <Input
                      id="cf-name" type="text" name="name" required
                      value={formData.name} onChange={handleChange}
                      data-testid="contact-form-name-input"
                      className="h-11 bg-surface border-line text-ink placeholder:text-ink-muted/60 focus:border-ink rounded-input"
                      placeholder={t.contact.form.name}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label htmlFor="cf-company" className="text-[12.5px] font-medium text-ink-muted">{t.contact.form.company}</label>
                    <Input
                      id="cf-company" type="text" name="company"
                      value={formData.company} onChange={handleChange}
                      data-testid="contact-form-company-input"
                      className="h-11 bg-surface border-line text-ink placeholder:text-ink-muted/60 focus:border-ink rounded-input"
                      placeholder={t.contact.form.company}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-5">
                  <div className="space-y-1.5">
                    <label htmlFor="cf-email" className="text-[12.5px] font-medium text-ink-muted">{t.contact.form.email}</label>
                    <Input
                      id="cf-email" type="email" name="email" required
                      value={formData.email} onChange={handleChange}
                      data-testid="contact-form-email-input"
                      className="h-11 bg-surface border-line text-ink placeholder:text-ink-muted/60 focus:border-ink rounded-input"
                      placeholder={t.contact.form.email}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label htmlFor="cf-phone" className="text-[12.5px] font-medium text-ink-muted">{t.contact.form.phone}</label>
                    <Input
                      id="cf-phone" type="tel" name="phone"
                      value={formData.phone} onChange={handleChange}
                      data-testid="contact-form-phone-input"
                      className="h-11 bg-surface border-line text-ink placeholder:text-ink-muted/60 focus:border-ink rounded-input"
                      placeholder={t.contact.form.phone}
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="cf-interest" className="text-[12.5px] font-medium text-ink-muted">{t.contact.form.interest}</label>
                  <select
                    id="cf-interest"
                    name="interest"
                    value={formData.interest}
                    onChange={handleChange}
                    data-testid="contact-form-interest-select"
                    className="w-full h-11 bg-surface border border-line text-ink rounded-input px-3 text-[14.5px] focus:outline-none focus:border-ink focus:ring-2 focus:ring-brand/20 transition-colors"
                  >
                    <option value="" disabled>{t.contact.form.interestPlaceholder || 'Valitse kiinnostuksen kohde'}</option>
                    {t.contact.form.interestOptions.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                  {t.contact.form.interestHelper && (
                    <p className="text-[12px] text-ink-muted leading-snug pt-1">{t.contact.form.interestHelper}</p>
                  )}
                </div>

                <div className="space-y-1.5">
                  <label htmlFor="cf-message" className="text-[12.5px] font-medium text-ink-muted">{t.contact.form.message}</label>
                  <Textarea
                    id="cf-message" name="message" required rows={6}
                    value={formData.message} onChange={handleChange}
                    data-testid="contact-form-message-input"
                    className="bg-surface border-line text-ink placeholder:text-ink-muted/60 focus:border-ink rounded-input resize-none"
                    placeholder={t.contact.form.message}
                  />
                </div>

                {status === 'success' && (
                  <div data-testid="contact-form-success" className="flex items-start gap-2.5 p-4 bg-brand-soft border border-brand/30 rounded-input text-brand-hover">
                    <CheckCircle className="w-5 h-5 mt-0.5 shrink-0" />
                    <span className="text-[14.5px]">{t.contact.form.success}</span>
                  </div>
                )}

                {status === 'error' && (
                  <div data-testid="contact-form-error" className="flex items-start gap-2.5 p-4 bg-[#FBEAE5] border border-[#E07A5F]/30 rounded-input text-[#9B3A1F]">
                    <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
                    <span className="text-[14.5px]">{t.contact.form.error}</span>
                  </div>
                )}

                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-form-submit-button"
                  className="w-full h-12 bg-brand hover:bg-brand-hover text-white font-medium rounded-pill transition-colors duration-150 disabled:opacity-60 disabled:cursor-not-allowed text-[15px] shadow-[0_8px_24px_rgba(47,111,94,0.18)]"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  {t.contact.form.submit}
                </Button>

                <p className="text-[12.5px] text-ink-muted text-center pt-1">
                  {t.contact.form.gdpr} {t.contact.reply}
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
